/**
 * Kość do gry — jedna dla całej platformy.
 *
 * Korpus to wycinek z arkusza `public/kostki.png`: sześć ścianek w siatce 3×2,
 * ta sama kość sfotografowana z góry. Oczka **nie** pochodzą z obrazka —
 * rysujemy je na wierzchu w kolorze akcentu, żeby barwa została w tokenie
 * i zmieniała się razem z graczem. Wypalona w PNG nie dałaby się przemalować,
 * a to właśnie kolor pionka ma tu mówić, czyja to kość.
 */

/** Które kratki siatki 3×3 są zapełnione dla danej wartości. */
export const UKLAD_OCZEK: Record<number, Array<[number, number]>> = {
  1: [[1, 1]],
  2: [
    [0, 0],
    [2, 2],
  ],
  3: [
    [0, 0],
    [1, 1],
    [2, 2],
  ],
  4: [
    [0, 0],
    [0, 2],
    [2, 0],
    [2, 2],
  ],
  5: [
    [0, 0],
    [0, 2],
    [1, 1],
    [2, 0],
    [2, 2],
  ],
  6: [
    [0, 0],
    [0, 2],
    [1, 0],
    [1, 2],
    [2, 0],
    [2, 2],
  ],
};

/*
 * Dopasowanie kropek do dołków w obrazku.
 *
 * Liczby **zmierzone** w pliku, nie dobrane na oko: skrypt znalazł obrys każdej
 * ścianki po kanale alfa i środki zagłębień po jasności. Zgadywanie nie miało
 * szans — dołki na zdjęciu nie leżą w idealnej siatce.
 *
 * Podmiana grafiki = ponowny pomiar.
 */
/** Odległość środka skrajnego oczka od krawędzi ścianki, w procentach. */
const MARGINES = 26.9;
/** Średnica oczka; nieco mniej niż zmierzone 17,6%, żeby zostawić widoczny rant dołka. */
const OCZKO = 15.5;

interface Props {
  wartosc: number;
  /** Podświetlona — w Kościach oznacza kość zatrzymaną. */
  zatrzymana?: boolean;
  rzucana?: boolean;
  /** Bez `onClick` kość jest tylko obrazkiem i nie udaje przycisku. */
  onClick?: () => void;
  disabled?: boolean;
  /**
   * `mala` dla Catana, gdzie kości są odczytem, a nie narzędziem.
   * `mikro` dla rzędu graczy w Perudzie: pięć kubków obok siebie na pięciu
   * calach nie mieści się w żadnym większym rozmiarze.
   */
  rozmiar?: "zwykla" | "mala" | "mikro";
  /** Barwa oczek. Domyślnie akcent bieżącego gracza. */
  kolor?: string;
}

export function Kostka({
  wartosc,
  zatrzymana = false,
  rzucana = false,
  onClick,
  disabled,
  rozmiar = "zwykla",
  kolor = "var(--color-akcent)",
}: Props) {
  const oczka = UKLAD_OCZEK[wartosc] ?? [];

  const tresc = (
    <span
      className="relative block h-full w-full bg-contain bg-center bg-no-repeat"
      /* Zero to pusta ścianka — w Kościach tyle widać, zanim ktokolwiek rzuci. */
      style={{ backgroundImage: `url(/kostki/${Math.min(6, Math.max(0, wartosc))}.png)` }}
    >
      {oczka.map(([wiersz, kolumna], i) => (
        <span
          key={i}
          className="absolute rounded-full"
          style={{
            width: `${OCZKO}%`,
            height: `${OCZKO}%`,
            left: `${MARGINES + kolumna * (50 - MARGINES) - OCZKO / 2}%`,
            top: `${MARGINES + wiersz * (50 - MARGINES) - OCZKO / 2}%`,
            backgroundColor: kolor,
          }}
        />
      ))}
    </span>
  );

  const klasy = [
    "aspect-square rounded-2xl transition-transform",
    rozmiar === "mikro" ? "w-7" : rozmiar === "mala" ? "w-12" : "w-full max-w-20",
    // Zatrzymana kość dostaje obwódkę, nie inne tło: korpus jest obrazkiem
    // i nie da się go przemalować.
    zatrzymana ? "ring-4 ring-akcent" : "",
    rzucana ? "kostka-rzut" : "",
  ];

  if (!onClick) {
    return (
      <span aria-label={`Kość ${wartosc}`} className={klasy.join(" ")}>
        {tresc}
      </span>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={`Kość ${wartosc}${zatrzymana ? ", zatrzymana" : ""}`}
      className={[...klasy, disabled ? "opacity-70" : "active:scale-95"].join(" ")}
    >
      {tresc}
    </button>
  );
}
